import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  CheckCircle,
  XCircle,
  Send,
  FileEdit,
  Clock,
  Info
} from 'lucide-react';

const PurchaseApprovalHistory = ({ order, compact = false }) => {
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventConfig = (type) => {
    const configs = {
      created: {
        icon: FileEdit,
        dotColor: 'bg-muted/60',
        badgeVariant: 'secondary'
      },
      submitted: {
        icon: Send,
        dotColor: 'bg-primary/100',
        badgeVariant: 'default'
      },
      approved: {
        icon: CheckCircle,
        dotColor: 'bg-success/100',
        badgeVariant: 'default'
      },
      rejected: {
        icon: XCircle,
        dotColor: 'bg-destructive/100',
        badgeVariant: 'destructive'
      },
      status_update: {
        icon: Clock,
        dotColor: 'bg-primary',
        badgeVariant: 'outline'
      }
    };
    return configs[type] || configs.status_update;
  };

  const getTimelineEvents = () => {
    const events = [];

    // Creation event
    if (order?.created_at) {
      events.push({
        id: 'created',
        title: '创建采购单',
        description: `由 ${order.created_by || '系统'} 创建`,
        timestamp: order.created_at,
        type: 'created'
      });
    }

    // Submission event
    if (order?.submitted_at) {
      events.push({
        id: 'submitted',
        title: '提交审批',
        description: `由 ${order.submitted_by || '用户'} 提交审批`,
        timestamp: order.submitted_at,
        type: 'submitted'
      });
    }

    // Approval event
    if (order?.approved_at) {
      events.push({
        id: 'approved',
        title: '审批通过',
        description: `由 ${order.approved_by || '管理员'} 批准`,
        timestamp: order.approved_at,
        type: 'approved'
      });
    }

    // Rejection event
    if (order?.rejected_at) {
      events.push({
        id: 'rejected',
        title: '审批拒绝',
        description: `由 ${order.rejected_by || '管理员'} 拒绝`,
        reason: order.rejection_reason,
        timestamp: order.rejected_at,
        type: 'rejected'
      });
    }

    // Status updates
    if (order?.status_updates && Array.isArray(order.status_updates)) {
      order.status_updates.forEach((update, index) => {
        events.push({
          id: `update_${index}`,
          title: `状态更新: ${update.status}`,
          description: `由 ${update.updated_by || '系统'} 更新`,
          note: update.note,
          timestamp: update.updated_at,
          type: 'status_update'
        });
      });
    }

    // Sort by timestamp
    return events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const events = getTimelineEvents();

  if (!events.length) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          暂无审批历史记录
        </AlertDescription>
      </Alert>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        {events.slice(-3).map((event) => {
          const config = getEventConfig(event.type);
          const IconComponent = config.icon;
          return (
            <div key={event.id} className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full ${config.dotColor} flex items-center justify-center`}>
                <IconComponent className="h-3.5 w-3.5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {event.title}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(event.timestamp)}
                </p>
              </div>
            </div>
          );
        })}
        {events.length > 3 && (
          <p className="text-xs text-muted-foreground pl-9">
            还有 {events.length - 3} 条历史记录...
          </p>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">审批历史</CardTitle>
      </CardHeader>
      <Separator />
      <CardContent className="pt-6">
        {/* Custom Timeline */}
        <div className="relative">
          {events.map((event, index) => {
            const config = getEventConfig(event.type);
            const IconComponent = config.icon;
            const isLast = index === events.length - 1;

            return (
              <div key={event.id} className="relative flex gap-4 pb-6">
                {/* Timeline connector line */}
                {!isLast && (
                  <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-border" />
                )}

                {/* Timeline dot */}
                <div className={`relative z-10 w-6 h-6 rounded-full ${config.dotColor} flex items-center justify-center flex-shrink-0`}>
                  <IconComponent className="h-3.5 w-3.5 text-white" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-2">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-semibold">
                      {event.title}
                    </span>
                    <Badge variant={config.badgeVariant} className="text-xs">
                      {event.type}
                    </Badge>
                  </div>

                  <p className="text-sm text-muted-foreground mb-1">
                    {event.description}
                  </p>

                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{formatDateTime(event.timestamp)}</span>
                  </div>

                  {event.reason && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertDescription className="text-sm">
                        <strong>拒绝原因:</strong> {event.reason}
                      </AlertDescription>
                    </Alert>
                  )}

                  {event.note && (
                    <div className="mt-2 p-2 bg-muted rounded border-l-2 border-primary">
                      <p className="text-sm">
                        <strong>备注:</strong> {event.note}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PurchaseApprovalHistory;
